/**
 * hooks/use-couple.ts
 *
 * TanStack Query hooks for couple management:
 * - useCouple()       — GET /couples/me (fetches couple + both users)
 * - useCreateCouple() — POST /couples/create
 * - useJoinCouple()   — POST /couples/join
 * - useUpdateCouple() — PATCH /couples/me
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  ApiSuccess,
  CoupleWithUsers,
  CoupleResponse,
  CreateCoupleInput,
  JoinCoupleInput,
  UpdateCoupleInput,
} from '@just-us/shared'
import { apiFetch } from '../lib/api-client'
import { setToken, getAuthPayload } from '../store/auth'

export const COUPLE_QUERY_KEY = ['couple'] as const

export const useCouple = () => {
  const payload = getAuthPayload()
  const hasCouple = !!payload?.couple_id

  return useQuery({
    queryKey: COUPLE_QUERY_KEY,
    queryFn: () => apiFetch<ApiSuccess<CoupleWithUsers>>('/couples/me'),
    enabled: hasCouple,
    staleTime: 30_000,
    refetchInterval: 30_000,
  })
}

export const useCreateCouple = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: CreateCoupleInput) =>
      apiFetch<ApiSuccess<{ token: string } & CoupleResponse>>('/couples/create', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: ({ data }) => {
      // Update token so couple_id is in subsequent requests
      setToken(data.token)
      queryClient.invalidateQueries({ queryKey: COUPLE_QUERY_KEY })
    },
  })
}

export const useJoinCouple = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: JoinCoupleInput) =>
      apiFetch<ApiSuccess<{ token: string } & CoupleResponse>>('/couples/join', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: ({ data }) => {
      setToken(data.token)
      queryClient.invalidateQueries({ queryKey: COUPLE_QUERY_KEY })
    },
  })
}

export const useUpdateCouple = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: UpdateCoupleInput) =>
      apiFetch<ApiSuccess<CoupleResponse>>('/couples/me', {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COUPLE_QUERY_KEY })
    },
  })
}
